import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { PermissionsMap } from "../../domain/entities/PermissionEntity";
import {
  ModuleCode,
  ActionCode,
  ACTION_CODES,
  MODULE_CODES,
} from "../../constants/permissions";
import { checkApprovedReview } from "../../utils/reviewWorkflow";
import { usePermissions } from "./usePermissions";

export interface UseJournalReviewDataOptions<T> {
  moduleCode: ModuleCode;
  entityType: "journal" | "ap_ar_journal";
  documentId: string | null;
  fetchDocument: (id: string) => Promise<T>;
  isPostedToZoho: (document: T) => boolean;
  initialDocument?: T | null;
}

export interface JournalReviewDataState {
  // Loading states
  isInitialDataLoading: boolean; // Permissions + Document
  isReviewDataLoading: boolean; // Review statuses
  isDataLoading: boolean; // Combined: true if either above is true

  // Permissions
  permissions: PermissionsMap | null;

  // Document
  document: any | null;
  isPostedToZoho: boolean;

  // Permission flags (computed after permissions loaded)
  canCreate: boolean;
  canUpdate: boolean;
  canUpdateAfterPush: boolean;
  canCreateRecurring: boolean;
  canDuplicateJournal: boolean;
  canCancelRecurringBeforeEnd: boolean;
  canDeleteAfterPush: boolean;
  canApArCreate: boolean;
  canApArDelete: boolean;

  // Review requirements (computed after permissions loaded)
  needsReviewForCreate: boolean;
  needsReviewForUpdate: boolean;
  needsReviewForUpdateAfterPush: boolean;
  needsReviewForPushToZoho: boolean;
  needsReviewForPushToZohoWithoutAttachment: boolean;
  needsReviewForCreateRecurring: boolean;
  needsReviewForDuplicateJournal: boolean;
  needsReviewForCancelRecurringBeforeEnd: boolean;
  needsReviewForDeleteAfterPush: boolean;
  needsReviewForApArCreate: boolean;
  needsReviewForApArDelete: boolean;

  // Review statuses (fetched conditionally) - Dynamic map for all actions
  // Key: action_code, Value: boolean | null (null = not checked, true = approved, false = not approved)
  reviewStatuses: Record<string, boolean | null>;

  // Helper function to get review status for any action
  getReviewStatus: (actionCode: ActionCode) => boolean | null;

  // Helper function to check if action has approved review (returns true if no review needed)
  hasApprovedReview: (actionCode: ActionCode) => boolean;

  // Final computed flags (computed after ALL data loaded)
  canActuallyEdit: boolean;
  canSaveAsDraft: boolean;
  canPushToZoho: boolean;

  // Errors
  error: Error | null;
}

/**
 * Hook to fetch journal review data (permissions + document + review statuses)
 * Similar to useCreditDebitNoteReviewData but for journals
 * Fetches everything in parallel using Promise.all() and conditionally checks review statuses
 *
 * @param options - Configuration options
 * @returns Review data state with all permission flags and review statuses
 */
export const useJournalReviewData = <T extends any>(
  options: UseJournalReviewDataOptions<T>,
): JournalReviewDataState => {
  const {
    moduleCode,
    entityType,
    documentId,
    fetchDocument,
    isPostedToZoho: checkIsPostedToZoho,
    initialDocument,
  } = options;

  // Use permissions from context instead of fetching directly
  const { permissions: contextPermissions, loading: permissionsLoading } =
    usePermissions();

  // Loading states
  const [isInitialDataLoading, setIsInitialDataLoading] =
    useState<boolean>(true);
  const [isReviewDataLoading, setIsReviewDataLoading] =
    useState<boolean>(false);

  // Data states
  const [permissions, setPermissions] = useState<PermissionsMap | null>(null);
  const [document, setDocument] = useState<T | null>(initialDocument || null);
  const [isPostedToZoho, setIsPostedToZoho] = useState<boolean>(false);

  // Permission flags
  const [canCreate, setCanCreate] = useState<boolean>(false);
  const [canUpdate, setCanUpdate] = useState<boolean>(false);
  const [canUpdateAfterPush, setCanUpdateAfterPush] = useState<boolean>(false);
  const [canPushToZoho, setCanPushToZoho] = useState<boolean>(false);
  const [canCreateRecurring, setCanCreateRecurring] = useState<boolean>(false);
  const [canDuplicateJournal, setCanDuplicateJournal] =
    useState<boolean>(false);
  const [canCancelRecurringBeforeEnd, setCanCancelRecurringBeforeEnd] =
    useState<boolean>(false);
  const [canDeleteAfterPush, setCanDeleteAfterPush] = useState<boolean>(false);
  const [canApArCreate, setCanApArCreate] = useState<boolean>(false);
  const [canApArDelete, setCanApArDelete] = useState<boolean>(false);

  // Review requirements
  const [needsReviewForCreate, setNeedsReviewForCreate] =
    useState<boolean>(false);
  const [needsReviewForUpdate, setNeedsReviewForUpdate] =
    useState<boolean>(false);
  const [needsReviewForUpdateAfterPush, setNeedsReviewForUpdateAfterPush] =
    useState<boolean>(false);
  const [needsReviewForPushToZoho, setNeedsReviewForPushToZoho] =
    useState<boolean>(false);
  const [
    needsReviewForPushToZohoWithoutAttachment,
    setNeedsReviewForPushToZohoWithoutAttachment,
  ] = useState<boolean>(false);
  const [needsReviewForCreateRecurring, setNeedsReviewForCreateRecurring] =
    useState<boolean>(false);
  const [needsReviewForDuplicateJournal, setNeedsReviewForDuplicateJournal] =
    useState<boolean>(false);
  const [
    needsReviewForCancelRecurringBeforeEnd,
    setNeedsReviewForCancelRecurringBeforeEnd,
  ] = useState<boolean>(false);
  const [needsReviewForDeleteAfterPush, setNeedsReviewForDeleteAfterPush] =
    useState<boolean>(false);
  const [needsReviewForApArCreate, setNeedsReviewForApArCreate] =
    useState<boolean>(false);
  const [needsReviewForApArDelete, setNeedsReviewForApArDelete] =
    useState<boolean>(false);

  // Review statuses - Dynamic map for all actions
  const [reviewStatuses, setReviewStatuses] = useState<
    Record<string, boolean | null>
  >({});

  // Error state
  const [error, setError] = useState<Error | null>(null);

  // Combined loading state
  const isDataLoading = isInitialDataLoading || isReviewDataLoading;

  // Use refs to store stable function references
  const fetchDocumentRef = useRef(fetchDocument);
  const checkIsPostedToZohoRef = useRef(checkIsPostedToZoho);

  // Update refs immediately when functions change (not in useEffect to avoid extra renders)
  // This ensures refs always point to the latest functions without triggering re-fetches
  fetchDocumentRef.current = fetchDocument;
  checkIsPostedToZohoRef.current = checkIsPostedToZoho;

  // Main data fetching effect - now waits for permissions from context
  useEffect(() => {
    let isMounted = true; // Prevent state updates if component unmounts

    const fetchAllData = async () => {
      // CRITICAL: Wait for permissions to be loaded from context
      if (permissionsLoading) {
        setIsInitialDataLoading(true);
        return; // Don't proceed until permissions are loaded
      }

      // If no permissions available yet, wait
      if (!contextPermissions) {
        setIsInitialDataLoading(true);
        return;
      }

      if (!documentId) {
        // No document ID, can't fetch document, but we have permissions
        if (isMounted) {
          setIsInitialDataLoading(true);
          setError(null);
        }

        try {
          // Use permissions from context (already loaded)
          const permissionsResult = contextPermissions;
          if (!isMounted) return;

          // Set permissions
          setPermissions(permissionsResult);

          const modulePermissions = permissionsResult[moduleCode] || {};
          const apArModulePermissions =
            permissionsResult[MODULE_CODES.AP_AR_JOURNAL] || {};

          const createInfo = modulePermissions[ACTION_CODES.CREATE] || {
            granted: false,
            requires_review: false,
          };
          const createRecurringInfo = modulePermissions[
            ACTION_CODES.CREATE_RECURRING
          ] || {
            granted: false,
            requires_review: false,
          };

          const pushToZohoInfo = modulePermissions[
            ACTION_CODES.PUSH_TO_ZOHO
          ] || {
            granted: false,
            requires_review: false,
          };

          const pushToZohoWithoutAttachmentInfo = modulePermissions[
            ACTION_CODES.PUSH_TO_ZOHO_WITHOUT_ATTACHMENT
          ] || {
            granted: false,
            requires_review: false,
          };

          const duplicateJournalInfo = modulePermissions[
            ACTION_CODES.DUPLICATE_JOURNAL
          ] || {
            granted: false,
            requires_review: false,
          };

          const cancelRecurringBeforeEndInfo = modulePermissions[
            ACTION_CODES.CANCEL_RECURRING_BEFORE_END
          ] || {
            granted: false,
            requires_review: false,
          };

          const deleteAfterPushInfo = modulePermissions[
            ACTION_CODES.DELETE_AFTER_PUSH_TO_ZOHO
          ] || {
            granted: false,
            requires_review: false,
          };
          const apArCreateInfo = apArModulePermissions[
            ACTION_CODES.AP_AR_CREATE
          ] || { granted: false, requires_review: false };
          const apArDeleteInfo = apArModulePermissions[
            ACTION_CODES.AP_AR_DELETE
          ] || { granted: false, requires_review: false };

          // Set permission flags
          setCanCreate(createInfo.granted);
          setCanPushToZoho(pushToZohoInfo.granted);
          setCanCreateRecurring(createRecurringInfo.granted);
          setCanDuplicateJournal(duplicateJournalInfo.granted);
          setCanCancelRecurringBeforeEnd(cancelRecurringBeforeEndInfo.granted);
          setCanDeleteAfterPush(deleteAfterPushInfo.granted);
          setCanApArCreate(apArCreateInfo.granted);
          setCanApArDelete(apArDeleteInfo.granted);

          // Set review requirements
          setNeedsReviewForCreate(createInfo.requires_review);
          setNeedsReviewForPushToZoho(pushToZohoInfo.requires_review);
          setNeedsReviewForPushToZohoWithoutAttachment(
            pushToZohoWithoutAttachmentInfo.requires_review,
          );
          setNeedsReviewForCreateRecurring(createRecurringInfo.requires_review);
          setNeedsReviewForDuplicateJournal(
            duplicateJournalInfo.requires_review,
          );
          setNeedsReviewForCancelRecurringBeforeEnd(
            cancelRecurringBeforeEndInfo.requires_review,
          );
          setNeedsReviewForDeleteAfterPush(deleteAfterPushInfo.requires_review);
          setNeedsReviewForApArCreate(apArCreateInfo.requires_review);
          setNeedsReviewForApArDelete(apArDeleteInfo.requires_review);

          setIsInitialDataLoading(false);

          // For new journals (no documentId), we can't check review statuses yet
          // Review statuses will be checked after the journal is created
          // Initialize empty review statuses map
          setReviewStatuses({});
          if (isMounted) {
            setIsReviewDataLoading(false);
          }
        } catch (err) {
          console.error("Error fetching permissions:", err);
          if (isMounted) {
            setIsInitialDataLoading(false);
            setIsReviewDataLoading(false);
            setError(
              err instanceof Error
                ? err
                : new Error("Failed to load permissions"),
            );
          }
        }
        return;
      }

      try {
        // ============================================
        // STEP 1: Use permissions from context + Fetch Document
        // ============================================
        if (isMounted) {
          setIsInitialDataLoading(true);
          setError(null);
        }

        // Use permissions from context (already loaded)
        const permissionsResult = contextPermissions;

        // Only fetch document (permissions already available from context)
        const documentResult = await fetchDocumentRef.current(documentId);

        if (!isMounted) return;

        // Set permissions and document
        setPermissions(permissionsResult);
        setDocument(documentResult);

        // Determine if posted to Zoho
        const isPosted = checkIsPostedToZohoRef.current(documentResult);
        setIsPostedToZoho(isPosted);

        // ============================================
        // STEP 2: Compute permission flags from loaded permissions
        // ============================================
        const modulePermissions = permissionsResult[moduleCode] || {};
        const apArModulePermissions =
          permissionsResult[MODULE_CODES.AP_AR_JOURNAL] || {};

        // Get permission info based on posted status
        const updateInfo = isPosted
          ? modulePermissions[ACTION_CODES.UPDATE_AFTER_PUSH]
          : modulePermissions[ACTION_CODES.UPDATE];

        const createInfo = modulePermissions[ACTION_CODES.CREATE] || {
          granted: false,
          requires_review: false,
        };
        const updateAfterPushInfo = modulePermissions[
          ACTION_CODES.UPDATE_AFTER_PUSH
        ] || {
          granted: false,
          requires_review: false,
        };
        const pushToZohoInfo = modulePermissions[ACTION_CODES.PUSH_TO_ZOHO] || {
          granted: false,
          requires_review: false,
        };
        const pushToZohoWithoutAttachmentInfo = modulePermissions[
          ACTION_CODES.PUSH_TO_ZOHO_WITHOUT_ATTACHMENT
        ] || {
          granted: false,
          requires_review: false,
        };
        const createRecurringInfo = modulePermissions[
          ACTION_CODES.CREATE_RECURRING
        ] || {
          granted: false,
          requires_review: false,
        };
        const duplicateJournalInfo = modulePermissions[
          ACTION_CODES.DUPLICATE_JOURNAL
        ] || {
          granted: false,
          requires_review: false,
        };

        const cancelRecurringBeforeEndInfo = modulePermissions[
          ACTION_CODES.CANCEL_RECURRING_BEFORE_END
        ] || {
          granted: false,
          requires_review: false,
        };

        const deleteAfterPushInfo = modulePermissions[
          ACTION_CODES.DELETE_AFTER_PUSH_TO_ZOHO
        ] || {
          granted: false,
          requires_review: false,
        };
        const apArCreateInfo = apArModulePermissions[
          ACTION_CODES.AP_AR_CREATE
        ] || {
          granted: false,
          requires_review: false,
        };
        const apArDeleteInfo = apArModulePermissions[
          ACTION_CODES.AP_AR_DELETE
        ] || {
          granted: false,
          requires_review: false,
        };

        // Set permission flags
        setCanCreate(createInfo.granted);
        setCanUpdate(updateInfo?.granted ?? false);
        setCanUpdateAfterPush(updateAfterPushInfo.granted);
        setCanPushToZoho(pushToZohoInfo.granted);
        setCanCreateRecurring(createRecurringInfo.granted);
        setCanDuplicateJournal(duplicateJournalInfo.granted);
        setCanCancelRecurringBeforeEnd(cancelRecurringBeforeEndInfo.granted);
        setCanDeleteAfterPush(deleteAfterPushInfo.granted);
        setCanApArCreate(apArCreateInfo.granted);
        setCanApArDelete(apArDeleteInfo.granted);

        // Set review requirements
        setNeedsReviewForCreate(createInfo.requires_review);
        setNeedsReviewForUpdate(updateInfo?.requires_review ?? false);
        setNeedsReviewForUpdateAfterPush(updateAfterPushInfo.requires_review);

        setNeedsReviewForPushToZoho(pushToZohoInfo.requires_review);
        setNeedsReviewForPushToZohoWithoutAttachment(
          pushToZohoWithoutAttachmentInfo.requires_review,
        );
        setNeedsReviewForCreateRecurring(createRecurringInfo.requires_review);
        setNeedsReviewForDuplicateJournal(duplicateJournalInfo.requires_review);
        setNeedsReviewForCancelRecurringBeforeEnd(
          cancelRecurringBeforeEndInfo.requires_review,
        );
        setNeedsReviewForDeleteAfterPush(deleteAfterPushInfo.requires_review);
        setNeedsReviewForApArCreate(apArCreateInfo.requires_review);
        setNeedsReviewForApArDelete(apArDeleteInfo.requires_review);

        setIsInitialDataLoading(false);

        // ============================================
        // STEP 3: Fetch review statuses (DYNAMIC - for ALL actions that require review)
        // ============================================
        setIsReviewDataLoading(true);

        // Dynamically check review status for ALL actions that:
        // 1. Have permission granted
        // 2. Require review
        const reviewPromises: Promise<{
          actionCode: string;
          approved: boolean;
        }>[] = [];

        // Iterate through all actions in the module permissions
        const reviewModulePermissions =
          entityType === MODULE_CODES.AP_AR_JOURNAL
            ? apArModulePermissions
            : modulePermissions;

        Object.keys(reviewModulePermissions).forEach((actionCode) => {
          const actionInfo = reviewModulePermissions[actionCode];

          // Check if this action requires review and user has permission
          if (actionInfo.granted && actionInfo.requires_review) {
            // For UPDATE action: only check if document is NOT posted
            // For UPDATE_AFTER_PUSH action: only check if document IS posted
            // For other actions: always check if granted and requires review

            let shouldCheckReview = false;

            if (actionCode === ACTION_CODES.UPDATE) {
              // Only check update review if document is NOT posted
              shouldCheckReview = !isPosted;
            } else if (actionCode === ACTION_CODES.UPDATE_AFTER_PUSH) {
              // Only check update_after_push review if document IS posted
              shouldCheckReview = isPosted;
            } else {
              // For all other actions (push_to_zoho, create, create_recurring, duplicate_journal)
              // Check review if granted and requires review
              shouldCheckReview = true;
            }

            if (shouldCheckReview) {
              reviewPromises.push(
                checkApprovedReview({
                  entity_type: entityType,
                  entity_id: parseInt(documentId),
                  action_code: actionCode,
                })
                  .then((result) => ({
                    actionCode,
                    approved: result.data.approved,
                  }))
                  .catch(() => ({
                    actionCode,
                    approved: false,
                  })),
              );
            }
          }
        });

        // Execute all review checks in parallel
        if (reviewPromises.length > 0) {
          const reviewResults = await Promise.all(reviewPromises);

          if (!isMounted) return;

          // Update review statuses map dynamically
          const newReviewStatuses: Record<string, boolean | null> = {};
          reviewResults.forEach((result) => {
            newReviewStatuses[result.actionCode] = result.approved;
          });

          setReviewStatuses(newReviewStatuses);
        } else {
          // No reviews needed, initialize empty map
          setReviewStatuses({});
        }

        setIsReviewDataLoading(false);
      } catch (err) {
        console.error("Error fetching journal review data:", err);
        if (isMounted) {
          setIsInitialDataLoading(false);
          setIsReviewDataLoading(false);
          setError(
            err instanceof Error ? err : new Error("Failed to load data"),
          );
        }
      }
    };

    fetchAllData();

    return () => {
      isMounted = false; // Cleanup
    };
    // Add contextPermissions and permissionsLoading to dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    documentId,
    moduleCode,
    entityType,
    contextPermissions,
    permissionsLoading,
  ]);

  // ============================================
  // STEP 4: Helper functions to get review status dynamically
  // ============================================

  // Helper function to get review status for any action
  const getReviewStatus = useCallback(
    (actionCode: ActionCode): boolean | null => {
      return reviewStatuses[actionCode] ?? null;
    },
    [reviewStatuses],
  );

  // Helper function to check if action has approved review
  // Returns true only if review is explicitly approved (status === true)
  // Returns false if review is required but not approved or not checked yet
  // Note: This function should be used in conjunction with needsReview flags
  // If needsReview is false, you don't need to check this function
  const hasApprovedReview = useCallback(
    (actionCode: ActionCode): boolean => {
      const status = reviewStatuses[actionCode];
      // Only return true if status is explicitly true (approved)
      // If status is null, it means review hasn't been checked yet or is pending
      // In that case, return false to require review
      return status === true;
    },
    [reviewStatuses],
  );

  // ============================================
  // STEP 5: Compute final flags with useMemo (using dynamic review statuses)
  // ============================================

  const canActuallyEdit = useMemo(() => {
    if (isDataLoading) return false;

    if (isPostedToZoho) {
      return (
        canUpdateAfterPush &&
        (!needsReviewForUpdateAfterPush ||
          hasApprovedReview(ACTION_CODES.UPDATE_AFTER_PUSH))
      );
    } else {
      return (
        canUpdate &&
        (!needsReviewForUpdate || hasApprovedReview(ACTION_CODES.UPDATE))
      );
    }
  }, [
    isDataLoading,
    isPostedToZoho,
    canUpdateAfterPush,
    canUpdate,
    needsReviewForUpdateAfterPush,
    needsReviewForUpdate,
    hasApprovedReview,
  ]);

  const canSaveAsDraft = useMemo(() => {
    if (isDataLoading) return false;
    if (isPostedToZoho) return false; // Can't save as draft if already posted
    return (
      canUpdate &&
      (!needsReviewForUpdate || hasApprovedReview(ACTION_CODES.UPDATE))
    );
  }, [
    isDataLoading,
    isPostedToZoho,
    canUpdate,
    needsReviewForUpdate,
    hasApprovedReview,
  ]);

  return {
    // Loading states
    isInitialDataLoading,
    isReviewDataLoading,
    isDataLoading,

    // Data
    permissions,
    document,
    isPostedToZoho,

    // Permission flags
    canCreate,
    canUpdate,
    canUpdateAfterPush,
    canCreateRecurring,
    canDuplicateJournal,
    canCancelRecurringBeforeEnd,
    canDeleteAfterPush,
    canApArCreate,
    canApArDelete,

    // Review requirements
    needsReviewForCreate,
    needsReviewForUpdate,
    needsReviewForUpdateAfterPush,
    needsReviewForPushToZoho,
    needsReviewForPushToZohoWithoutAttachment,
    needsReviewForCreateRecurring,
    needsReviewForDuplicateJournal,
    needsReviewForCancelRecurringBeforeEnd,
    needsReviewForDeleteAfterPush,
    needsReviewForApArCreate,
    needsReviewForApArDelete,

    // Review statuses - Dynamic map for all actions
    reviewStatuses,
    getReviewStatus,
    hasApprovedReview,

    // Final computed flags
    canActuallyEdit,
    canSaveAsDraft,
    canPushToZoho,

    // Errors
    error,
  };
};
